/**
 * Payment service for integrating with Paystack and PayFast
 */

interface PaystackConfig {
  email: string;
  amount: number; // in kobo (ZAR cents)
  metadata: {
    productId: string;
    [key: string]: any;
  };
}

interface PaystackResponse {
  reference: string;
  status: string;
  message: string;
}

/**
 * Initiate a Paystack payment
 * Note: Requires Paystack inline script to be loaded
 */
export function initiatePaystackPayment(config: PaystackConfig): Promise<PaystackResponse> {
  return new Promise((resolve, reject) => {
    // Check if Paystack is loaded
    if (!(window as any).PaystackPop) {
      reject(new Error('Paystack script not loaded. Add script tag to index.html'));
      return;
    }

    const handler = (window as any).PaystackPop.setup({
      key: import.meta.env.VITE_PAYSTACK_PUBLIC_KEY,
      email: config.email,
      amount: config.amount,
      currency: 'ZAR',
      metadata: config.metadata,
      callback: function(response: PaystackResponse) {
        // Payment successful
        console.log('Payment successful:', response.reference);
        resolve(response);
      },
      onClose: function() {
        // Payment cancelled
        reject(new Error('Payment cancelled by user'));
      }
    });

    handler.openIframe();
  });
}

/**
 * Initiate a PayFast payment
 * PayFast uses form submission, so this will redirect the user
 */
export function initiatePayfastPayment(
  merchantId: string,
  merchantKey: string,
  amount: number,
  itemName: string,
  returnUrl: string,
  cancelUrl: string,
  notifyUrl: string,
  customStr1?: string
) {
  // PayFast uses form submission
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = 'https://www.payfast.co.za/eng/process'; // or sandbox URL for testing

  const fields: Record<string, string> = {
    merchant_id: merchantId,
    merchant_key: merchantKey,
    amount: amount.toFixed(2),
    item_name: itemName,
    return_url: returnUrl,
    cancel_url: cancelUrl,
    notify_url: notifyUrl,
  };

  if (customStr1) {
    fields.custom_str1 = customStr1;
  }

  Object.entries(fields).forEach(([key, value]) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = key;
    input.value = value;
    form.appendChild(input);
  });

  document.body.appendChild(form);
  form.submit();
}

/**
 * Helper to convert ZAR amount to Paystack kobo (cents)
 */
export function zarToKobo(amountZar: number): number {
  return Math.round(amountZar * 100);
}

/**
 * Helper to convert kobo to ZAR
 */
export function koboToZar(amountKobo: number): number {
  return amountKobo / 100;
}

/**
 * Format ZAR currency for display
 */
export function formatZAR(amount: number): string {
  return `R${amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
}

/**
 * Verify payment status (to be called after redirect back from gateway)
 */
export async function verifyPayment(reference: string): Promise<boolean> {
  // This would typically call a Cloud Function to verify the payment
  // For now, just check if reference exists in URL params
  const urlParams = new URLSearchParams(window.location.search);
  const urlReference = urlParams.get('reference');

  return urlReference === reference;
}

/**
 * Get payment reference from URL params (after redirect)
 */
export function getPaymentReferenceFromUrl(): string | null {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('reference');
}

/**
 * Check if payment was successful from URL params
 */
export function isPaymentSuccessFromUrl(): boolean {
  const urlParams = new URLSearchParams(window.location.search);
  const status = urlParams.get('status');
  return status === 'success' || status === 'completed';
}
